import React, { useRef, useEffect } from 'react';
import type { ChartData } from '../../types/analytics';

interface Props {
  data: ChartData;
  title: string;
  size?: number;
  showLegend?: boolean;
  showPercentages?: boolean;
}

export default function PieChart({ 
  data, 
  title, 
  size = 300, 
  showLegend = true, 
  showPercentages = true 
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !data.datasets.length) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = size * window.devicePixelRatio;
    canvas.height = size * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    const radius = Math.min(centerX, centerY) - 20;

    const dataset = data.datasets[0];
    const values = dataset.data;
    const total = values.reduce((sum, value) => sum + value, 0);

    if (total === 0) return;

    const colors = [
      '#8b5cf6',
      '#ec4899',
      '#f59e0b',
      '#fbbf24',
      '#a78bfa',
      '#60a5fa',
      '#3b82f6',
      '#6366f1',
      '#f9a8d4',
      '#86efac'
    ];

    let currentAngle = -Math.PI / 2; // Start from top

    values.forEach((value, index) => {
      const sliceAngle = (value / total) * 2 * Math.PI;
      const color = colors[index % colors.length];

      // Draw slice
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      ctx.fill();

      // Draw percentage label
      if (showPercentages && value > 0) {
        const percentage = ((value / total) * 100).toFixed(1);
        const labelAngle = currentAngle + sliceAngle / 2;
        const labelX = centerX + Math.cos(labelAngle) * (radius * 0.7);
        const labelY = centerY + Math.sin(labelAngle) * (radius * 0.7);

        ctx.fillStyle = 'white';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${percentage}%`, labelX, labelY);
      }

      currentAngle += sliceAngle;
    });

  }, [data, size, showPercentages]);

  return (
    <div className="card-pastel p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">{title}</h3>
      <div className="flex items-center justify-center space-x-6">
        <canvas
          ref={canvasRef}
          className="flex-shrink-0"
          style={{ width: `${size}px`, height: `${size}px` }}
        />
        
        {showLegend && (
          <div className="space-y-2">
            {data.labels.map((label, index) => {
              const colors = [
                '#8b5cf6', '#ec4899', '#f59e0b', '#fbbf24', '#a78bfa',
                '#60a5fa', '#3b82f6', '#6366f1', '#f9a8d4', '#86efac'
              ];
              const color = colors[index % colors.length];
              const value = data.datasets[0].data[index];
              const total = data.datasets[0].data.reduce((sum, v) => sum + v, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';

              return (
                <div key={index} className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-sm text-gray-600">
                    {label}: {percentage}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
